/**
 * validatePostcodeSelector.js
 *
 * A set of validation functions to handle validation logic for postcode selectors within a form
 * submission process. It ensures that required fields are filled and certain conditions are met
 * before the form can be successfully submitted.
 *
 * Usage:
 *
 * This function can be used to validate PostcodeSelector component fields on form submission.
 *
 * Example:
 *
 * import validatePostcodeSelector from 'components/cef_form/PostcodeSelector/validatePostcodeSelector';
 *
 * const Step3 = () => {
 *   const { watch, setValue, errors, setError, clearErrors, setCurrentStep } = useContext(HookFormContext);
 *
 *   const companyTypeValue = 0; // Example value for company type
 *
 *   const mainRouteValidation = [
 *     {
 *       prefix: 'manualCompany',
 *       searched: manualCompanyPostcodeSearched,
 *       clearFieldErrors: companyNumberLookupSuccess,
 *       clearPostcodeSearchErrors: manualCompanyHasPersistedData,
 *     },
 *     {
 *       prefix: 'billing',
 *       searched: billingPostcodeSearched,
 *       clearFieldErrors: billingAddressMatchesRegistered,
 *       clearPostcodeSearchErrors: billingHasPersistedData,
 *     },
 *   ];
 *
 *   const soleTraderRouteValidation = [
 *     { prefix: 'home', searched: homePostcodeSearched },
 *     {
 *       prefix: 'previous',
 *       searched: previousPostcodeSearched,
 *       clearFieldErrors: propertyDuration,
 *     },
 *   ];
 *
 *   const postcodeSelectorValidations = {
 *     0: mainRouteValidation, // LC
 *     1: soleTraderRouteValidation, // Sole Trader
 *     5: mainRouteValidation, // LLP
 *     6: mainRouteValidation, // PLC
 *   };
 *
 *   return (
 *     <StepTemplate
 *       validate={() => {
 *         const validationConfig = postcodeSelectorValidations[companyTypeValue];
 *         return validatePostcodeSelector(validationConfig, watch, errors, setError, clearErrors);
 *       }}
 *     >
 *       <p>This is content</p>
 *     </StepTemplate>
 *   );
 * };
 *
 * export default Step3;
 *
 * Parameters:
 * - validationConfig: An array of objects, each containing configuration for a specific postcode selector
 * - watch: A function to watch form field values
 * - errors: An object containing current validation errors
 * - setError: A function to manually set an error on a field
 * - clearErrors: A function to clear errors on specific fields
 *
 * Returns:
 * - A boolean indicating whether the form is valid (true) or not (false).
 */

import {
  postcodeRequired,
  addressSelectionRequired,
  addressHouseNumber,
  addressLine1Required,
  addressLine4Required,
  invalidPostcode,
  postcodeSearchRequired,
} from 'components/cef_form/PostcodeSelector/errorMessages';

const validatePostcodeSelector = async (
  validationConfig,
  watch,
  errors,
  setError,
  clearErrors,
) => {
  let isValid = true;

  // FIELDS SETUP

  // Define error messages for each field type
  const allFields = {
    SearchPostcode: postcodeRequired,
    AddressSelector: addressSelectionRequired,
    Number: addressHouseNumber,
    Line1: addressLine1Required,
    Line4: addressLine4Required,
    Postcode: invalidPostcode,
  };

  // Create validation configurations for every field
  const postcodeFields = validationConfig.flatMap(({ prefix }) =>
    Object.entries(allFields).map(([field, errorMessage]) => ({
      inputField: `${prefix}${field}`,
      validationField: `${prefix}${field}Valid`,
      errorMessage,
      type: 'required',
    })),
  );

  // Create validation configurations for postcode search fields
  const postcodeSearchFields = validationConfig.flatMap(
    ({ prefix, searched }) => [
      {
        inputField: `${prefix}SearchPostcode`,
        validationField: `${prefix}SearchPostcodeValid`,
        isSearched: searched,
        errorMessage: postcodeSearchRequired,
        type: 'notTriggered',
      },
    ],
  );

  // FIELD VALIDATION FUNCTIONS

  // Process field validation based on type
  const processFieldValidation = ({
    inputField,
    fieldValid,
    isSearched,
    errorMessage,
    type,
  }) => {
    if (
      (type === 'required' && !fieldValid) ||
      (type === 'notTriggered' && fieldValid && !isSearched)
    ) {
      setError(inputField, {
        type,
        message: errorMessage,
      });

      isValid = false;
    }
  };

  // Validate fields based on provided configurations
  const validateFields = fields => {
    fields.forEach(({ validationField, ...field }) => {
      const fieldValid = !!watch(validationField);
      processFieldValidation({ ...field, fieldValid });
    });
  };

  // Clear errors for fields when a certain condition is met
  const clearConditionalFieldErrors = config => {
    config.forEach(({ prefix, clearFieldErrors }) => {
      if (clearFieldErrors) {
        Object.keys(allFields).forEach(field => {
          clearErrors(`${prefix}${field}`);
        });

        isValid = true;
      }
    });
  };

  // Clear errors for postcode search field when a certain condition is met
  const clearConditionalPostcodeSearchErrors = config => {
    config.forEach(({ prefix, clearPostcodeSearchErrors }) => {
      if (clearPostcodeSearchErrors) {
        const postcodeSearchField = `${prefix}SearchPostcode`;
        clearErrors(postcodeSearchField);
      }
    });
  };

  // Clear fatal errors on postcode search fields before submission
  const clearFatalErrors = config => {
    config.forEach(({ prefix }) => {
      const postcodeSearchField = `${prefix}SearchPostcode`;
      const addressSelectorField = `${prefix}AddressSelector`;
      const fieldError = errors[postcodeSearchField];

      if (fieldError?.type === 'fatal') {
        clearErrors(postcodeSearchField);
        clearErrors(addressSelectorField);
      }
    });
  };

  // START VALIDATIONS

  validateFields(postcodeFields); // Validate all fields
  validateFields(postcodeSearchFields); // Validate search field to check that 'Find Address' was triggered

  clearConditionalFieldErrors(validationConfig); // Clear field errors based on condition
  clearConditionalPostcodeSearchErrors(validationConfig); // Clear field errors based on condition
  clearFatalErrors(validationConfig); // Clear fatal errors on search fields before submission so they are non-blocking

  return isValid;
};

export default validatePostcodeSelector;
